import { useCallback, useId } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useIsDesktop } from "../../hooks/useIsDesktop.js";
import { useResizablePanel } from "../../hooks/useResizablePanel.js";
import { useDialogA11y } from "../../hooks/useDialogA11y.js";
import { useBackToClose } from "../../hooks/useBackToClose.js";
import { PANEL_BAR } from "./panelBar.js";
import PanelResizeHandle from "./PanelResizeHandle.jsx";

/**
 * Panel lateral deslizante desde la derecha.
 *
 * @param {object}    props
 * @param {boolean}   props.isOpen
 * @param {string}    props.title
 * @param {Function}  props.onClose
 * @param {ReactNode} props.children
 * @param {"sm"|"md"|"lg"|"xl"} [props.size="lg"] - preset de ancho responsive
 * @param {boolean} [props.bodyScroll=true]
 *   true (default): el body es un contenedor con `overflow-y-auto`; el panel scrollea como un todo.
 *   false: el body es un flex column sin scroll propio — los hijos manejan su propio scroll
 *   y sticky footer. Usar cuando el panel necesite barra de acciones siempre visible al pie
 *   y solo la lista central deba rodar.
 * @param {boolean} [props.titleVisible=true]
 *   false: el título sigue existiendo para el lector de pantalla (es el nombre
 *   accesible del diálogo) pero no se dibuja. Es para los paneles cuyo cuerpo
 *   YA muestra la identidad del registro —`DetailPanel` y su bloque de icono +
 *   nombre + estado—: ahí la barra repetía el mismo texto dos centímetros más
 *   arriba. No usarlo en un panel cuyo cuerpo no diga de quién es.
 */
const SIZE_CLASSES = {
  sm: "w-full sm:w-96",
  md: "w-full sm:w-[28rem] lg:w-[34rem]",
  lg: "w-full sm:w-[34rem] lg:w-[45rem] xl:w-[52rem]",
  xl: "w-full sm:w-[40rem] lg:w-[56rem] xl:w-[64rem]",
};

// Fallback por preset para el drag cuando el panel aún no midió su ancho real.
const SIZE_DEFAULT_WIDTH = { sm: 384, md: 448, lg: 720, xl: 896 };

// Apple-style easing: arranca con momentum, desacelera suavemente al final.
const PANEL_EASE = [0.32, 0.72, 0, 1];

export default function SlidePanel({
  isOpen,
  title,
  onClose,
  children,
  size = "lg",
  bodyScroll = true,
  titleVisible = true,
}) {
  const titleId = useId();
  const { dialogRef, dialogProps } = useDialogA11y({
    isOpen,
    onClose,
    labelledBy: titleId,
  });

  useBackToClose(isOpen, onClose);

  const widthClass = SIZE_CLASSES[size] ?? SIZE_CLASSES.lg;
  const isDesktop = useIsDesktop();
  // Ancho compartido por preset: todos los drawers `lg` recuerdan el mismo
  // ancho custom, etc. `width` null = sin resize del usuario → mandan las
  // clases responsive del preset.
  const { width, isResizing, panelRef, handleProps } = useResizablePanel({
    storageKey: `panel-w:slide-${SIZE_CLASSES[size] ? size : "lg"}`,
    defaultWidth: SIZE_DEFAULT_WIDTH[size] ?? SIZE_DEFAULT_WIDTH.lg,
    minWidth: 320,
    maxWidth: 1200,
    handleSide: "left",
  });

  // El <aside> lo necesitan dos dueños: useResizablePanel para medir el ancho
  // al arrastrar, y useDialogA11y para el foco. Un callback ref alimenta a los
  // dos sin que ninguno tenga que saber del otro.
  const setPanelNode = useCallback(
    (node) => {
      panelRef.current = node;
      dialogRef.current = node;
    },
    [panelRef, dialogRef],
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: PANEL_EASE }}
            className="fixed inset-0 z-[310] scrim-soft backdrop-blur-[1px]"
            onClick={onClose}
          />

          <motion.aside
            ref={setPanelNode}
            {...dialogProps}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              duration: 0.42,
              ease: PANEL_EASE,
            }}
            style={isDesktop && width != null ? { width } : undefined}
            // El panel tapa la pantalla entera, TopNav incluida: en la app nativa
            // (edge-to-edge) reserva él mismo la barra de gestos acá abajo, y la
            // de estado su barra de arriba (PANEL_BAR), que es la que pinta
            // esa franja, como hace TopNav. En web env() vale 0.
            className={`fixed right-0 top-0 h-full z-[320] pb-[env(safe-area-inset-bottom)] ${widthClass} bg-surface-1 sheen-2 border-l border-border shadow-elevated flex flex-col`}
          >
            <PanelResizeHandle
              side="left"
              isResizing={isResizing}
              {...handleProps}
            />
            {/*
              Sin título visible NO se dibuja la barra: quedaba una franja de
              53 px con nada más que la ✕ y una línea debajo, justo encima del
              bloque de identidad del cuerpo. El botón pasa a flotar sobre la
              esquina —el `aside` es `fixed`, así que le sirve de contenedor y
              el botón se queda quieto mientras el cuerpo rueda— y el h3 se va
              a `sr-only`, que es lo único que la barra aportaba ahí: el nombre
              accesible del diálogo.
            */}
            {titleVisible ? (
              <div className={`flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] shrink-0 ${PANEL_BAR}`}>
                <h3 id={titleId} className="text-sm font-semibold text-text-primary">
                  {title}
                </h3>
                <CloseButton onClose={onClose} />
              </div>
            ) : (
              <>
                <h3 id={titleId} className="sr-only">
                  {title}
                </h3>
                {/* Sobre la barra que dibuja el cuerpo (DetailPanel), debajo de la de estado. */}
                <div className="absolute right-3 top-[calc(0.75rem+env(safe-area-inset-top))] z-20">
                  <CloseButton onClose={onClose} />
                </div>
              </>
            )}

            <div
              className={
                bodyScroll
                  ? "flex-1 overflow-y-auto p-4"
                  : "flex-1 min-h-0 flex flex-col p-4"
              }
            >
              {children}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

/** La ✕ del panel — misma pinta esté en la barra o flotando en la esquina. */
function CloseButton({ onClose }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className="touch-target flex items-center justify-center w-7 h-7 rounded-md text-text-dim hover:text-text-primary hover:bg-surface-2 transition-colors"
      aria-label="Cerrar panel"
    >
      <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
        close
      </span>
    </button>
  );
}
